/**
 * A pessoa e a ficha dela (spec 030). Ate 23/09 o painel so tinha caminho pro PLANO: pra chegar numa pessoa era
 * preciso abrir uma matricula dela, e quem tinha dois planos aparecia duas vezes sem nada dizer que era a mesma
 * gente. A entrada "Traders" existia no menu desde sempre, sem tela, entao nao aparecia.
 *
 * Aqui e o cadastro, nao o plano: dados, e-mails, acesso e a trilha. A equipe corrige dados e e-mails daqui (`EmailsDaPessoa.tsx`). Os planos aparecem como lista curta com link,
 * porque o detalhe deles mora na ficha da matricula (decisao 4).
 *
 * Nenhuma rota nova: tudo o que esta aqui ja existia no backend (secao 1 da spec; as de e-mail e dados, spec 001, 4.7).
 * Spec 039: a secao "Comprovantes", com os que a pessoa mandou pela area, conferidos ou recusados ali mesmo.
 * Spec 057, secao 4 (Vitor, 23/10): os planos antigos se editam e se excluem direto na tabela de planos.
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, CampoSelecao, CampoTextoLongo, Carregando, Fatos, IconeVoltar, LinkInterno, Pilha, Secao, Segmentado, Selo, Tabela, Vazio, formatarData, formatarDataHora, formatarDia, formatarTelefone, hojeEmSaoPaulo, POR_PAGINA, type Coluna } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { erroDe, nomeDaAcao, quem, useAcao, useApi, usePapel, type MatriculaPainel } from "../api";
import { salvarArquivo } from "../baixar";
import { useAmbiente } from "../contexto";
import { ComprovantesDaPessoa } from "./Comprovantes";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { GerenciarPlano } from "./GerenciarPlano";
import { EditarPlanoAntigo, ExcluirPlanoAntigo, usePlanosAntigosDoCliente, type PlanoAntigo } from "./PlanosAntigos";
import { EditarDados, ReenviarAcesso, SecaoDeEmails, detalheDaTrilha, type AcessoDaFicha, type AuditoriaDaFicha, type EmailDaFicha, type PessoaDaFicha } from "./EmailsDaPessoa";
import { usePagina } from "../pagina";
import { comSubconta } from "../subconta";

/** O que `GET /v1/admin/pessoas` devolve hoje. Conferido no backend antes de escrever a tela. */
type PessoaDaLista = { id: string; nome: string; cpf: string; email_principal: string | null; telefone: string | null; tem_senha: boolean; origem: string; criado_em: string; primeiro_acesso_em: string | null; ultimo_acesso_em: string | null; planos: { nome: string; status: string; subconta?: string | null }[] };
type Ficha = {
  pessoa: PessoaDaFicha;
  emails: EmailDaFicha[];
  acesso: AcessoDaFicha;
  /** Uso da area nos ultimos 30 dias (spec 062, 5.5). */
  uso?: { dias_30: number; minutos_por_dia: number | null; logins_30: number };
  auditoria: AuditoriaDaFicha[];
};
/** O mesmo que a lista de matriculas ja usa: `GET /v1/admin/pessoas/:id/matriculas` devolve `paraPainel`. */
type PlanoDaPessoa = MatriculaPainel;

const STATUS: Record<string, { rotulo: string; tom: "sucesso" | "alerta" | "info" | "neutro" }> = {
  ATIVA: { rotulo: "Ativa", tom: "sucesso" },
  AGUARDANDO_INICIO: { rotulo: "Aguardando início", tom: "info" },
  SUSPENSA: { rotulo: "Suspensa", tom: "alerta" },
  ENCERRADA: { rotulo: "Encerrada", tom: "neutro" },
};

// ---------------------------------------------------------------- lista

/**
 * Cadastros > Clientes. Busca por nome, CPF, e-mail ou telefone (Vitor, 15/10); "Nunca entraram" mostra quem tem plano
 * em andamento e nunca abriu a area, pra equipe chamar; e a lista sai em planilha com a mesma busca e o mesmo filtro.
 */
export function PaginaTraders() {
  const { ler, arquivo } = useApi();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  // A busca e o filtro moram no estado da tela e so depois vao pro endereco: o React Router troca o endereco como
  // transicao, entao limpar a busca e trocar o filtro em seguida partia do endereco velho e trazia a busca de volta
  // (visto no e2e, 15/10). O endereco continua servindo pra voltar e pra mandar o link.
  const [busca, setBusca] = useState(() => params.get("q") ?? "");
  const [filtro, setFiltro] = useState<"todos" | "nunca-entrou">(() => (params.get("filtro") === "nunca-entrou" ? "nunca-entrou" : "todos"));
  // De 20 em 20 (spec 058, Fase 2), com a pagina no endereco. Ela vai pro endereco no MESMO setParams da busca e do
  // filtro: com dois no mesmo instante (este e o do `usePagina` voltando pra 1), os dois partiam do endereco velho e o
  // ultimo apagava o primeiro. Mudou a busca ou o filtro, volta pra pagina 1; abrir o link com ?pagina=3 mantem a 3.
  const pag = usePagina("pagina");
  const anterior = useRef({ busca, filtro });
  useEffect(() => {
    const mudou = anterior.current.busca !== busca || anterior.current.filtro !== filtro;
    anterior.current = { busca, filtro };
    const n = new URLSearchParams({ ...(busca ? { q: busca } : {}), ...(filtro !== "todos" ? { filtro } : {}) });
    const pagina = params.get("pagina");
    if (pagina && !mudou) n.set("pagina", pagina);
    if (n.toString() !== params.toString()) setParams(n, { replace: true });
  }, [busca, filtro, params, setParams]);
  const [exportando, setExportando] = useState(false);
  const [erroExportar, setErroExportar] = useState<string>();
  const consulta = new URLSearchParams({ ...(busca.trim() ? { busca: busca.trim() } : {}), ...(filtro !== "todos" ? { filtro } : {}), pagina: String(pag.pagina), por_pagina: String(POR_PAGINA) }).toString();
  const q = useQuery({ queryKey: ["pessoas", consulta], queryFn: () => ler<{ itens: PessoaDaLista[]; total: number; pagina: number }>(`/v1/admin/pessoas?${consulta}`), placeholderData: (anterior) => anterior });
  const exportar = async () => {
    setErroExportar(undefined);
    setExportando(true);
    try {
      const so = new URLSearchParams({ ...(busca.trim() ? { busca: busca.trim() } : {}), ...(filtro !== "todos" ? { filtro } : {}) }).toString();
      salvarArquivo(await arquivo(`/v1/admin/pessoas/exportar${so ? `?${so}` : ""}`), `clientes-${hojeEmSaoPaulo()}.csv`);
    } catch (e) {
      setErroExportar(erroDe(e));
    } finally {
      setExportando(false);
    }
  };
  const colunas: Coluna<PessoaDaLista>[] = [
    { chave: "nome", rotulo: "Cliente", celula: (p) => <><LinkInterno href={`/traders/${p.id}`}>{p.nome}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{p.cpf}</span></> },
    { chave: "contato", rotulo: "Contato", celula: (p) => <>{p.email_principal ?? <span className="mtc-texto-2">sem e-mail</span>}{p.telefone && <span className="mtc-tabela__apoio mtc-mono">{formatarTelefone(p.telefone)}</span>}</> },
    { chave: "planos", rotulo: "Planos", celula: (p) => (p.planos.length ? <>{p.planos.slice(0, 2).map((m, i) => <span key={i} className={i ? "mtc-tabela__apoio" : undefined} style={i ? undefined : { display: "block" }}>{comSubconta(m.nome, m.subconta)} <Selo tom={STATUS[m.status]?.tom ?? "neutro"}>{STATUS[m.status]?.rotulo ?? m.status}</Selo></span>)}{p.planos.length > 2 && <span className="mtc-tabela__apoio">e mais {p.planos.length - 2}</span>}</> : <span className="mtc-texto-2">Sem plano</span>) },
    { chave: "acesso", rotulo: "Acesso à área", celula: (p) => (p.ultimo_acesso_em ? <><span className="mtc-mono">{formatarDataHora(p.ultimo_acesso_em)}</span><span className="mtc-tabela__apoio">último acesso</span></> : <Selo tom={p.planos.some((m) => m.status !== "ENCERRADA") ? "alerta" : "neutro"}>{p.tem_senha ? "nunca entrou" : "sem senha, nunca entrou"}</Selo>) },
    { chave: "desde", rotulo: "Cadastrado em", celula: (p) => <span className="mtc-mono">{formatarData(p.criado_em)}</span> },
  ];
  const itens = q.data?.itens ?? [];
  return (
    <Pilha espaco={4}>
      <CabecalhoDePagina titulo="Clientes" descricao="Quem tem cadastro neste ambiente. O cadastro é a pessoa; os planos dela ficam na ficha." acoes={<Botao variante="secundario" carregando={exportando} disabled={!itens.length} onClick={() => void exportar()}>Exportar planilha</Botao>} />
      <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <Segmentado rotulo="Mostrar" nome="filtro-de-clientes" valor={filtro} aoMudar={setFiltro} opcoes={[{ valor: "todos", rotulo: "Todos" }, { valor: "nunca-entrou", rotulo: "Nunca entraram" }]} />
        <div style={{ flex: "1 1 280px" }}><Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, e-mail, telefone ou subconta" /></div>
      </div>
      {filtro === "nunca-entrou" && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Têm plano em andamento e nunca abriram a área (nem pela senha, nem pelo link do e-mail). Vale chamar: talvez o e-mail de acesso não tenha chegado.</span>}
      {erroExportar && <Aviso tipo="erro">{erroExportar}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          linhas={itens}
          chave={(p) => p.id}
          aoClicar={(p) => navegar(`/traders/${p.id}`)}
          colunas={colunas}
          paginacao={{ pagina: q.data?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: q.data?.total ?? 0 }}
          vazio={<Vazio titulo={filtro === "nunca-entrou" ? "Todo mundo já entrou" : busca ? "Ninguém com esse dado" : "Ninguém cadastrado ainda"}>{filtro === "nunca-entrou" ? "Quem tem plano em andamento já abriu a área pelo menos uma vez." : busca ? "Tente outro nome, CPF, e-mail, telefone ou subconta." : "O cadastro nasce da compra, da importação ou da mão da equipe."}</Vazio>}
        />
      )}
    </Pilha>
  );
}

// ---------------------------------------------------------------- ficha

export function PaginaTrader() {
  const pagPlanos = usePagina("pagina_planos");
  const { id = "" } = useParams();
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const { modulos } = useAmbiente();
  const navegar = useNavigate();
  // O aviso de quem voltou da planilha de planos antigos (spec 057) chega pelo estado da navegacao.
  const [feito, setFeito] = useState<string | undefined>((useLocation().state as { feito?: string } | null)?.feito);
  const [conceder, setConceder] = useState(false);
  const [modo, setModo] = useState<"um" | "antigos">("um");
  const [plano, setPlano] = useState("");
  const [motivo, setMotivo] = useState("");
  const acao = useAcao();
  const q = useQuery({ queryKey: ["pessoa", id], queryFn: () => ler<Ficha>(`/v1/admin/pessoas/${id}`) });
  const planos = useQuery({ queryKey: ["pessoa-matriculas", id], queryFn: () => ler<{ itens: PlanoDaPessoa[] }>(`/v1/admin/pessoas/${id}/matriculas`) });
  const catalogo = useQuery({ queryKey: ["planos"], queryFn: () => ler<{ itens: { slug: string; nome: string; ativo: boolean }[] }>("/v1/admin/planos") });
  // Plano antigo (spec 057) se edita e se exclui aqui; plano comprado nao (excluir apagaria a venda), e o detalhe dele mora na ficha do plano.
  const antigos = usePlanosAntigosDoCliente(id, modulos.includes("etapas") && Boolean(planos.data?.itens.some((m) => m.origem === "ANTIGO")));
  const [editando, setEditando] = useState<PlanoAntigo | null>(null);
  const [excluindo, setExcluindo] = useState<{ id: string; nome: string } | null>(null);
  // Gerenciar plano direto da ficha (Vitor, 29/10): o mesmo dialogo da pagina do plano, sem precisar achar o link no nome.
  const [gerenciando, setGerenciando] = useState<string | null>(null);

  if (q.isLoading) return <Carregando compacto texto="Abrindo o cadastro" />;
  if (!q.data) return <Pilha espaco={4}><LinkInterno href="/traders"><IconeVoltar tamanho={16} /> Voltar</LinkInterno><Aviso tipo="erro">{q.error ? erroDe(q.error) : "Cadastro não encontrado."}</Aviso></Pilha>;
  const { pessoa, emails, acesso, auditoria, uso } = q.data;
  const atualizar = () => q.refetch();
  const mostrar = (texto: string) => { setFeito(texto); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const mudouPlano = (texto: string) => { void planos.refetch(); void q.refetch(); mostrar(texto); };
  const meus = planos.data?.itens ?? [];
  const disponiveis = (catalogo.data?.itens ?? []).filter((p) => p.ativo);

  return (
    <Pilha espaco={4}>
      <LinkInterno href="/traders"><IconeVoltar tamanho={16} /> Voltar</LinkInterno>
      <CabecalhoDePagina titulo={pessoa.nome} descricao="Os dados de cadastro desta pessoa. Os planos têm ficha própria." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}

      <Secao titulo="Cadastro" acoes={<EditarDados pessoa={pessoa} aoMudar={atualizar} aoFeito={mostrar} />}>
        <Fatos
          itens={[
            { rotulo: "CPF", valor: <span className="mtc-mono">{pessoa.cpf}</span>, mono: true },
            { rotulo: "Telefone", valor: pessoa.telefone ? formatarTelefone(pessoa.telefone) : "não informado" },
            { rotulo: "Nascimento", valor: pessoa.nascimento ? formatarDia(pessoa.nascimento.slice(0, 10)) : "não informado" },
            { rotulo: "Origem", valor: pessoa.origem },
            { rotulo: "Cadastrado em", valor: formatarData(pessoa.criado_em) },
          ]}
        />
        {/* Decisao 7: o CPF e a identidade (spec 001). Dizer isso aqui evita descobrir no erro da rota. */}
        <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>O CPF não se edita: ele é a identidade da pessoa. Se estiver errado, fale com o suporte antes de mexer.</p>
      </Secao>

      <Secao titulo="Acesso" acoes={<ReenviarAcesso pessoa={pessoa} emails={emails} acesso={acesso} aoFeito={mostrar} />}>
        <Fatos
          itens={[
            { rotulo: "Senha", valor: acesso.tem_senha ? "definida" : "ainda não criou" },
            { rotulo: "Último acesso", valor: acesso.ultimo_login_em ? formatarDataHora(acesso.ultimo_login_em) : "nunca entrou" },
            { rotulo: "Sessões abertas", valor: String(acesso.sessoes_ativas) },
            ...(uso ? [
              { rotulo: "Uso nos últimos 30 dias", valor: uso.dias_30 ? `${uso.dias_30} ${uso.dias_30 === 1 ? "dia" : "dias"}, ${uso.minutos_por_dia} min por dia` : "não usou a área" },
              { rotulo: "Logins nos últimos 30 dias", valor: String(uso.logins_30) },
            ] : []),
          ]}
        />
      </Secao>

      <SecaoDeEmails pessoa={pessoa} emails={emails} acesso={acesso} auditoria={auditoria} aoMudar={atualizar} aoFeito={mostrar} />

      <Secao titulo="Planos" acoes={pode("ADMIN") ? <Botao tamanho="pequeno" onClick={() => { setFeito(undefined); setConceder(true); }}>Adicionar plano</Botao> : undefined}>
        {planos.isLoading ? <Carregando compacto texto="Buscando os planos" /> : (
          <Tabela
            paginacao={pagPlanos}
            linhas={meus}
            chave={(m) => m.id}
            colunas={[
              { chave: "plano", rotulo: "Plano", celula: (m) => <LinkInterno href={`/matriculas/${m.id}`}>{m.plano.nome}</LinkInterno> },
              { chave: "status", rotulo: "Situação", celula: (m) => <Selo tom={STATUS[m.status]?.tom ?? "neutro"}>{STATUS[m.status]?.rotulo ?? m.status}</Selo> },
              // Spec 059: a subconta do plano em andamento. Sem nenhuma na lista (a Global nao tem), sem a coluna.
              ...(meus.some((m) => m.subconta) ? [{ chave: "subconta", rotulo: "Subconta", celula: (m: PlanoDaPessoa) => (m.subconta ? <span className="mtc-mono">{m.subconta}</span> : null) }] : []),
              { chave: "origem", rotulo: "Origem", celula: (m) => m.origem },
              // A data da compra, nao a do cadastro: o plano antigo adicionado hoje comecou meses atras.
              { chave: "desde", rotulo: "Desde", celula: (m) => <span className="mtc-mono">{formatarData(m.comprada_em ?? m.criado_em)}</span> },
              ...(pode("ADMIN") ? [{
                chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (m: PlanoDaPessoa) => {
                  if (m.origem !== "ANTIGO") {
                    // Encerrado se corrige desfazendo o encerramento, na pagina do plano.
                    if (m.status === "ENCERRADA") return null;
                    return <Botao tamanho="pequeno" variante="secundario" aria-label={`Gerenciar ${m.plano.nome}`} onClick={() => { setFeito(undefined); setGerenciando(m.id); }}>Gerenciar plano</Botao>;
                  }
                  const antigo = antigos.data?.itens.find((a) => a.id === m.id);
                  return (
                    <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
                      <Botao tamanho="pequeno" variante="secundario" disabled={!antigo} aria-label={`Editar ${m.plano.nome}`} onClick={() => { setFeito(undefined); setEditando(antigo!); }}>Editar</Botao>
                      <Botao tamanho="pequeno" variante="fantasma" aria-label={`Excluir ${m.plano.nome}`} onClick={() => { setFeito(undefined); setExcluindo({ id: m.id, nome: `${m.plano.nome} de ${pessoa.nome}` }); }}>Excluir</Botao>
                    </div>
                  );
                },
              }] : []),
            ]}
            vazio={<Vazio titulo="Nenhum plano">Esta pessoa tem cadastro, mas nenhum plano neste ambiente.</Vazio>}
          />
        )}
      </Secao>

      <ComprovantesDaPessoa pessoaId={pessoa.id} aoDecidir={(t) => { setFeito(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} />

      <Secao titulo="Trilha">
        <Tabela
          linhas={auditoria.slice(0, 20)}
          chave={(a) => `${a.em}-${a.acao}-${detalheDaTrilha(a)}`}
          colunas={[
            { chave: "em", rotulo: "Quando", celula: (a) => <span className="mtc-mono">{formatarDataHora(a.em)}</span> },
            { chave: "acao", rotulo: "O que", celula: (a) => <>{nomeDaAcao(a.acao)}{detalheDaTrilha(a) && <span className="mtc-tabela__apoio" style={{ overflowWrap: "anywhere" }}>{detalheDaTrilha(a)}</span>}</> },
            { chave: "ator", rotulo: "Por", celula: (a) => quem(a.ator) },
          ]}
          vazio={<Vazio titulo="Sem registro">Nada aconteceu com este cadastro ainda.</Vazio>}
        />
      </Secao>

      <EditarPlanoAntigo plano={editando} aoFechar={() => setEditando(null)} aoSalvo={mudouPlano} />
      <ExcluirPlanoAntigo plano={excluindo} aoFechar={() => setExcluindo(null)} aoExcluido={mudouPlano} />
      <GerenciarPlano matriculaId={gerenciando ?? ""} aberto={Boolean(gerenciando)} aoFechar={() => setGerenciando(null)} aoSalvar={() => { setGerenciando(null); mudouPlano("Plano atualizado. O cliente não é avisado: ele vê o plano como ficou na área."); }} />

      {/* Decisao 5 e 6: usa a cortesia que ja existe, e avisa o que o ato faz ANTES de fazer. */}
      <DialogoDeAcao
        aberto={conceder}
        aoFechar={() => setConceder(false)}
        titulo="Adicionar plano"
        descricao={`Para ${pessoa.nome}, sem compra e sem pagamento ligado.`}
        botao={modo === "antigos" ? "Abrir a planilha" : "Adicionar plano"}
        podeEnviar={modo === "antigos" || (Boolean(plano) && motivo.trim().length >= 5)}
        aoAbrir={() => { setModo("um"); setPlano(""); setMotivo(""); acao.limpar(); }}
        aoEnviar={async () => (modo === "antigos" ? navegar(`/traders/${pessoa.id}/planos-antigos`) : postar("/v1/admin/matriculas", { pessoa_id: pessoa.id, plano_slug: plano, motivo: motivo.trim() }))}
        aoConcluir={() => { if (modo === "antigos") return; void planos.refetch(); void q.refetch(); setFeito("Plano adicionado. A pessoa recebeu o e-mail com o acesso."); }}
      >
        {/* Planos antigos (spec 057): so com etapas (a Prop), como os repasses antigos. */}
        {modulos.includes("etapas") && <Segmentado rotulo="O que adicionar" nome="modo-de-adicionar" valor={modo} aoMudar={setModo} opcoes={[{ valor: "um", rotulo: "Um plano" }, { valor: "antigos", rotulo: "Vários planos antigos" }]} />}
        {modo === "antigos" ? (
          <Aviso tipo="info">
            Abre uma planilha pra adicionar de uma vez os planos que {pessoa.nome} teve na plataforma antiga: ativos ou encerrados, com as datas de cada etapa e a subconta. Você escolhe uma vez só se a pessoa é avisada.
          </Aviso>
        ) : (
          <>
            <Aviso tipo="alerta">
              O plano nasce sem pagamento ligado e <strong>a pessoa recebe um e-mail</strong> avisando que ganhou o plano. Fica na trilha com o seu nome e o motivo.
            </Aviso>
            <CampoSelecao rotulo="Plano" valor={plano} aoMudar={setPlano} required opcoes={[{ valor: "", rotulo: "Escolha o plano" }, ...disponiveis.map((p) => ({ valor: p.slug, rotulo: p.nome }))]} />
            <CampoTextoLongo rotulo="Por que está adicionando" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: plano perdido na importação; cortesia combinada com o cliente." dica="Fica na trilha, com o seu nome." />
          </>
        )}
      </DialogoDeAcao>
    </Pilha>
  );
}
